// src/pages/Ads/Reserve/components/ReserveGroup.jsx
import React from "react";
import { ReserveButton } from "./ButtonGroup";
import { STRAIGHT_OPTIONS } from "../../pages/Ads/components/ReserveComponents/straightOptions"

const dayMap = {
  Mon: "월",
  Tue: "화",
  Wed: "수",
  Thu: "목",
  Fri: "금",
  Sat: "토",
  Sun: "일",
};

const formatTimeKorean = (time) => {
  if (!time) return "";
  const [hStr = "0", mStr = "0"] = time.split(":");
  const hour = Number(hStr) || 0;
  const minute = Number(mStr) || 0;
  const ampm = hour < 12 ? "오전" : "오후";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${ampm} ${hour12}:${String(minute).padStart(2, "0")}`;
};

const chunk = (arr = [], size = 3) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

// endDate가 오늘 이전이면 true
const isExpiredEndDate = (dateStr) => {
  if (!dateStr) return false;

  // "2025.09.30" 같이 들어올 수도 있으니 . 을 - 로 치환
  const normalized = dateStr.replace(/\./g, "-").trim();

  const d = new Date(
    normalized.includes("T") ? normalized : `${normalized}T00:00:00`
  );
  if (Number.isNaN(d.getTime())) return false;

  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  return d.getTime() < startOfToday.getTime();
};

// 날짜 형식
const formatMonthlyDay = (value) => {
  // 숫자
  if (typeof value === "number") {
    if (value === 0) return "d";       // 0 → d
    return `${value}일`;
  }

  if (typeof value === "string") {
    const v = value.trim();

    // 0d / 0D → d
    if (v.toLowerCase() === "0d") return "d";

    // yyyy-mm-dd 또는 yyyy-mm-ddT...
    const ymdMatch = v.match(/^(\d{4}-\d{2}-\d{2})/i);
    if (ymdMatch) {
      const d = new Date(ymdMatch[1] + "T00:00:00");
      if (!Number.isNaN(d.getTime())) {
        return `${d.getDate()}일`;
      }
    }

    // "1" ~ "31"
    if (/^\d{1,2}$/.test(v)) {
      return `${Number(v)}일`;
    }

    // 그 외: 그냥 원본 + "일"
    return `${v}일`;
  }

  // 타입 예외: 그냥 문자열로
  return String(value);
};

const STRAIGHT_TITLE_MAP = STRAIGHT_OPTIONS.reduce((acc, opt) => {
  acc[opt.key] = opt.title;
  return acc;
}, {});


/**
 * 예약 카드
 *
 * props:
 * - title: string (상단 제목)
 * - startDate: string (YYYY.MM.DD 또는 원하는 포맷)
 * - endDate: string
 * - weeklyDays?: string[]  (["Mon","Tue",...] / "월","화",... 둘 다 허용)
 * - monthlyDays?: string[]  (["1","8",...]
 * - uploadTimes: string[]  (["12:00","12:00", ...])
 * - onDelete: () => void
 * - onEdit: () => void
 */
const ReserveCard = ({
  title = "빈 제목",
  // startDate,
  endDate,
  weeklyDays = [],
  monthlyDays = [],
  uploadTimes = [],
  straight,
  updatedAt,
  onDelete,
  onEdit,
}) => {
  const hasWeekly = weeklyDays && weeklyDays.length > 0;
  const hasMonthly = monthlyDays && monthlyDays.length > 0;
  const timeRows = chunk(uploadTimes, 3); // 한 줄에 최대 3개씩

  const displayDays = weeklyDays.map((d) => dayMap[d] || d);

  const expired = isExpiredEndDate(endDate);

  const labelClass = expired
    ? "text-text-disabled"
    : "text-text-subtle";

  const straightTitle = STRAIGHT_TITLE_MAP[straight] || "바로가기 없음";

  return (
    <div className={labelClass + " text-text-disabled rounded-[20px] bg-surface-light-white border border-border-default p-[12px] flex flex-col gap-[12px]"}>
      {/* 제목 */}
      <p className="text-t4">{title}</p>

      <div className="space-y-[8px]">
        {/* 기간 */}
        {/* <div className="flex gap-[8px]">
          <span className="w-[50px] text-c1">기간</span>
          <span className="text-c2">
            {startDate} ~ {endDate}
          </span>
        </div> */}

        {/* 요일 (옵션) */}
        {hasWeekly && (
          <div className="flex gap-[8px]">
            <span className="w-[50px] text-c1">요일</span>
            <div className="flex flex-wrap gap-x-[8px] text-c2">
              {displayDays.map((d, idx) => (
                <span key={idx}>{d}</span>
              ))}
            </div>
          </div>
        )}

        {/* 날짜 (옵션) */}
        {hasMonthly && (
          <div className="flex gap-[8px]">
            <span className="w-[50px] text-c1">날짜</span>
            <div className="flex flex-wrap gap-x-[8px] text-c2">
              {monthlyDays.map((d, idx) => (
                <span key={idx}>{formatMonthlyDay(d)}</span>
              ))}
            </div>
          </div>
        )}

        {/* 시간 */}
        <div className="flex gap-[8px]">
          <span className="w-[50px] text-c1">시간</span>
          <div className="flex flex-col gap-y-[8px] text-c2">
            {timeRows.map((row, rowIdx) => (
              <div key={rowIdx} className="flex flex-wrap gap-x-[12px]">
                {row.map((t, i) => (
                  <span key={i}>{formatTimeKorean(t)}</span>
                ))}
              </div>
            ))}
          </div>
        </div>


        {/* 바로가기 텍스트 */}
        {(straight && (straight !== "none")) && (
          <div className="flex gap-[8px]">
            <span className="w-[50px] text-c1">바로가기</span>
            <span className="text-c2">{straightTitle}</span>
          </div>
        )}

        {/* 생성일 */}
        {updatedAt && (
          <div className="flex gap-[8px]">
            <span className="w-[50px] text-c1">생성일</span>
            <span className="text-c2">{updatedAt}</span>
          </div>
        )}

      </div>


      {/* 버튼 영역 */}
      {expired ? (
        // 만료: 삭제만
        <div className="flex gap-[8px]">
          <ReserveButton label="삭제" onClick={onDelete} />
        </div>
      ) : (
        // 일반: 삭제 + 수정
        <div className="flex gap-[8px]">
          <ReserveButton label="삭제" onClick={onDelete} />
          <ReserveButton label="수정" type="blue" onClick={onEdit} />
        </div>
      )}
    </div>
  );
};

export default ReserveCard;
